import { useTranslation } from 'next-i18next'
// Libs
import styled from 'styled-components'
import React, { useContext } from 'react'

// Comps
import {
  WhatsappShareButton,
  WhatsappIcon,
  TwitterShareButton,
  TwitterIcon,
  FacebookShareButton,
  FacebookIcon,
  EmailShareButton,
  EmailIcon,
} from 'react-share'

import { Modal, Props as ModalProps } from './Modal'
import { ModalContentWrapper } from './ModalContentWrapper'
import {
  EventHookInterface,
  AnalyticsEventShareEvent,
  Toast,
  ToastType,
} from '../../types'
import { copyTextToClipboard, icons, show } from '../../utils'
import { CTA } from '../CTA'
import { H3 } from '../H3'
import { H5 } from '../H5'
// import { ModalContentWrapper } from '../ModalContentWrapper'
import { P } from '../P'
import { useAnalyticsEvent } from '../../hooks/analyticsEvent'
import { ToastContext } from '../../context'
import { ModalCloseButton } from '../ModalCloseButton'

// Styles
import { shareEventModalStyles } from './shareEventModalStyles'

const EventModalContentContainer = styled(ModalContentWrapper)`
  ${shareEventModalStyles}
`

// Defs
interface Props extends ModalProps {
  eventHook: EventHookInterface
  header: string
  text?: string | React.ReactNode
  showCalendarLink: boolean
  showCalendarClick?: () => void
}

export const ShareEventModal: React.FC<Props> = (props) => {
  const { t } = useTranslation('common')

  // Props.
  const {
    header,
    text,
    close,
    eventHook,
    showCalendarLink,
    showCalendarClick,
  } = props

  // Global State
  const analyticsEvent = useAnalyticsEvent()
  const { addToast } = useContext(ToastContext)

  // calc
  const href = eventHook.value.event_url
  const title_on_klyk = `Klyk: "${eventHook.value.title}"`

  // Funcs
  // On user interaction send message to intercom that user used share no modal
  const sendAnalyticsEvent = (type: string) => {
    const eventProp: AnalyticsEventShareEvent = {
      eventId: eventHook.value.id,
      type: type,
      source: 'Share event modal',
    }
    analyticsEvent.eventsShareEvent(eventProp)
  }

  // When the user copies the url link to the clipboard
  const onCopyLink = () => {
    copyTextToClipboard(href)
    addToast({
      id: `copied`,
      title: t(`Link copied to clipboard.`, `Link copied to clipboard.`),
      toastType: ToastType.Success,
      dismissTime: 8000,
    } as Toast)
    sendAnalyticsEvent('Clipboard')
  }

  // ..
  return (
    <Modal close={close}>
      <EventModalContentContainer>
        <ModalCloseButton onClick={close} />

        {/* Text */}
        <span className="text-block">
          <H3 className="header">{header}</H3>
          {show(text !== undefined) && (
            <>
              {text || null}
              {/* <P className="reminder-text">{}</P> */}
            </>
          )}
        </span>

        {/* CTA For add to calendar */}
        {show(showCalendarLink === true) && (
          <>
            <CTA
              className="calendar-cta green modal-cta"
              onClick={showCalendarClick}>
              <div className="icon-container">
                <svg
                  width="19"
                  height="22"
                  viewBox="0 0 19 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M15.6667 3.66602H2.83333C1.82081 3.66602 1 4.48683 1 5.49935V18.3327C1 19.3452 1.82081 20.166 2.83333 20.166H15.6667C16.6792 20.166 17.5 19.3452 17.5 18.3327V5.49935C17.5 4.48683 16.6792 3.66602 15.6667 3.66602Z"
                    stroke="white"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12.9167 1.83398V5.50065"
                    stroke="white"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M5.58331 1.83398V5.50065"
                    stroke="white"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M1 9.16602H17.5"
                    stroke="white"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <title>{t(`Calendar`, `Calendar`)}</title>
                </svg>
              </div>{' '}
              {t(`ShareEventModal-1337383974`, `Add to calendar`)}
            </CTA>
          </>
        )}

        {/* Share Links */}
        <H5 className="share-event-on-header">{t(`Share`, `Share`)}</H5>
        <div className="share-network">
          <FacebookShareButton
            className="shareButton"
            url={href}
            quote={title_on_klyk}
            onClick={() => {
              sendAnalyticsEvent('Facebook')
            }}>
            <FacebookIcon
              size={32}
              bgStyle={{ fill: '#E5E6EB' }}
              iconFillColor="#333333"
              round
            />
          </FacebookShareButton>

          <TwitterShareButton
            className="shareButton"
            url={href}
            title={title_on_klyk}
            onClick={() => {
              sendAnalyticsEvent('Twitter')
            }}>
            <TwitterIcon
              size={32}
              bgStyle={{ fill: '#E5E6EB' }}
              iconFillColor="#333333"
              round
            />
          </TwitterShareButton>

          <WhatsappShareButton
            className="shareButton"
            url={href}
            title={title_on_klyk}
            separator=" -> "
            onClick={() => {
              sendAnalyticsEvent('WhatsApp')
            }}>
            <WhatsappIcon
              size={32}
              bgStyle={{ fill: '#E5E6EB' }}
              iconFillColor="#333333"
              round
            />
          </WhatsappShareButton>

          <EmailShareButton
            className="shareButton"
            url={href}
            subject={title_on_klyk}
            body={title_on_klyk}
            onClick={() => {
              sendAnalyticsEvent('Email')
            }}>
            <EmailIcon
              size={32}
              bgStyle={{ fill: '#E5E6EB' }}
              iconFillColor="#333333"
              round
            />
          </EmailShareButton>

          {/* Custom Copy Link Button */}
          <button
            className="shareButton share-button-link"
            onClick={onCopyLink}>
            <div className="circle">
              <svg
                width="17"
                height="17"
                viewBox="0 0 17 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M7.08334 9.20833C7.38753 9.61501 7.77563 9.95151 8.22131 10.195C8.66699 10.4385 9.15982 10.5833 9.66638 10.6196C10.1729 10.6558 10.6814 10.5828 11.1572 10.4053C11.633 10.2278 12.0651 9.95001 12.4242 9.59083L14.5492 7.46583C15.1943 6.79787 15.5513 5.90324 15.5432 4.97462C15.5352 4.04601 15.1627 3.15771 14.506 2.50106C13.8494 1.84441 12.9611 1.47193 12.0325 1.46386C11.1039 1.45579 10.2092 1.81277 9.54125 2.45792L8.32292 3.66917"
                  stroke="black"
                  strokeWidth="1.25"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9.91667 7.79167C9.61248 7.38499 9.22438 7.0485 8.7787 6.805C8.33302 6.56151 7.84019 6.41671 7.33363 6.38043C6.82707 6.34415 6.31863 6.41724 5.8428 6.59474C5.36697 6.77224 4.93488 7.04999 4.57584 7.40917L2.45084 9.53417C1.80569 10.2021 1.44871 11.0968 1.45678 12.0254C1.46485 12.954 1.83733 13.8423 2.49398 14.4989C3.15063 15.1556 4.03893 15.5281 4.96754 15.5361C5.89616 15.5442 6.79079 15.1872 7.45875 14.5421L8.67 13.3308"
                  stroke="black"
                  strokeWidth="1.25"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </button>
        </div>
      </EventModalContentContainer>
    </Modal>
  )
}
